import React, { useState, useEffect, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";

import { type PlayerStats, type GoalkeeperStats } from "@/api/ranking";
import { getPointsBadge } from "@/lib/utils";
import { Loader } from "../../common/loader";
import { ComparisonEngineMultiSelect } from "./ComparisonEngineMultiSelect";
import ComparisonEngineStatsTable from "./ComparisonEngineStatsTable";
import ComparisonEngineChart, { type RadarPoint } from "./ComparisonEngineChart";
import { ComparisonStatsMobileCards } from "./ComparisonStatsMobileCards";

type ComparisonPlayerStats = PlayerStats | GoalkeeperStats;

const generalStats: Record<string, string> = {
    pointsDefensive: "Defensive Points",
    pointsOffensive: "Offensive Points",
    pointsTeamPlay: "Team Play Points",
    pointsEffort: "Effort Points",
    pointsBonus: "Bonus Points",
    totalPoints: "Total Points",
};

const goalkeeperStats: Record<string, string> = {
    pointsGSAx: "GSAx Points",
    pointsHighDangerShots: "High Danger Shots",
    pointsLowDangerShots: "Low Danger Shots",
    pointsLateralPassShots: "Lateral Pass Shots",
    pointsScreenedShots: "Screened Shots",
    totalPoints: "Total Points",
};

const maxPointsByCategory: Record<string, number> = {
    pointsDefensive: 36,
    pointsOffensive: 24,
    pointsTeamPlay: 30,
    pointsEffort: 10,
    pointsBonus: 10,
};

const maxGoalkeeperPointsByCategory: Record<string, number> = {
    pointsGSAx: 50,
    pointsHighDangerShots: 30,
    pointsLowDangerShots: 20,
    pointsLateralPassShots: 25,
    pointsScreenedShots: 25,
};

interface RowData {
    stat: string;
    playerA: string | number;
    playerB: string | number;
    isTotal?: boolean;
}

export interface TeamGroupConfig {
    label: string;
    isActive: boolean;
    onActivate: () => void;
    isLoading?: boolean;
}

interface ComparisonEngineProps {
    data: ComparisonPlayerStats[];
    availablePlayersForSelection?: ComparisonPlayerStats[];
    isLoading?: boolean;
    matchIds?: number[];
    selectedPlayers?: ComparisonPlayerStats[];
    viewMode?: string;
    setSelectedPlayers?: (
        players: ComparisonPlayerStats[] | ((prev: ComparisonPlayerStats[]) => ComparisonPlayerStats[])
    ) => void;
    teamGroups?: TeamGroupConfig[];
}

const formatValue = (value: number): string => {
    return value % 1 === 0 ? value.toString() : value.toFixed(1);
};

const ComparisonEngine: React.FC<ComparisonEngineProps> = ({
    data,
    availablePlayersForSelection,
    isLoading = false,
    matchIds = [],
    selectedPlayers = [],
    viewMode = "",
    setSelectedPlayers,
    teamGroups,
}) => {
    const [selected, setSelected] = useState<ComparisonPlayerStats[]>(selectedPlayers);

    const playersForDropdown = useMemo(() => {
        const players = availablePlayersForSelection || data;
        // Deduplicate players by playerId
        const uniquePlayers = players.reduce((acc, player) => {
            if (!acc.find((p) => p.playerId === player.playerId)) {
                acc.push(player);
            }
            return acc;
        }, [] as ComparisonPlayerStats[]);
        return uniquePlayers;
    }, [availablePlayersForSelection, data]);

    const isGoalkeeperComparison = selected.length > 0 && selected[0].role === "GK";

    useEffect(() => {
        if (JSON.stringify(selected) !== JSON.stringify(selectedPlayers)) {
            setSelected(selectedPlayers);
        }
    }, [selectedPlayers]);

    useEffect(() => {
        if (setSelectedPlayers && JSON.stringify(selected) !== JSON.stringify(selectedPlayers)) {
            setSelectedPlayers(selected);
        }
    }, [selected, setSelectedPlayers]);

    const currentStats = useMemo(() => {
        return isGoalkeeperComparison ? goalkeeperStats : generalStats;
    }, [isGoalkeeperComparison]);

    const statKeys = useMemo(() => {
        return Object.keys(currentStats) as (keyof ComparisonPlayerStats)[];
    }, [currentStats]);

    const chartStatKeys = useMemo(() => {
        return Object.keys(currentStats).filter((key) => key !== "totalPoints") as (keyof ComparisonPlayerStats)[];
    }, [currentStats]);

    const maxPoints = useMemo(() => {
        return isGoalkeeperComparison ? maxGoalkeeperPointsByCategory : maxPointsByCategory;
    }, [isGoalkeeperComparison]);

    const playerAName = selected[0] ? `${selected[0].firstName} ${selected[0].lastName}` : "";
    const playerBName = selected[1] ? `${selected[1].firstName} ${selected[1].lastName}` : "";

    const { columns, data: rowData } = useMemo(() => {
        const cols: ColumnDef<RowData>[] = [
            { accessorKey: "stat", header: "", cell: (info) => <>{info.getValue<string>()}</> },
            {
                accessorKey: "playerA",
                header: playerAName,
                cell: (info) => {
                    const value = info.getValue();
                    const isTotal = (info.row.original as RowData).isTotal;
                    if (isTotal && viewMode === "average" && typeof value === "number" && value > 0) {
                        const { bg, text, border } = getPointsBadge(value);
                        return (
                            <div
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${bg} ${text} rounded font-bold text-sm border ${border}`}
                            >
                                {formatValue(value)}
                            </div>
                        );
                    }
                    return <>{typeof value === "number" ? formatValue(value) : value}</>;
                },
            },
            {
                accessorKey: "playerB",
                header: playerBName,
                cell: (info) => {
                    const value = info.getValue();
                    const isTotal = (info.row.original as RowData).isTotal;
                    if (isTotal && viewMode === "average" && typeof value === "number" && value > 0) {
                        const { bg, text, border } = getPointsBadge(value);
                        return (
                            <div
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${bg} ${text} rounded font-bold text-sm border ${border}`}
                            >
                                {formatValue(value)}
                            </div>
                        );
                    }
                    return <>{typeof value === "number" ? formatValue(value) : value}</>;
                },
            },
        ];

        const rows: RowData[] = statKeys.map((key) => ({
            stat: currentStats[key] ?? "",
            playerA: selected[0] ? ((selected[0][key] as number) ?? 0) : "",
            playerB: selected[1] ? ((selected[1][key] as number) ?? 0) : "",
            isTotal: key === "totalPoints",
        }));

        return { columns: cols, data: rows };
    }, [selected, statKeys, currentStats, playerAName, playerBName, viewMode]);

    const chartData: RadarPoint[] = useMemo(() => {
        const calculatePercentage = (value: number, category: string): number => {
            const maxPointsValue = maxPoints[category];
            if (!maxPointsValue) return 0;
            return Math.round((value / maxPointsValue) * 100);
        };

        if (selected.length === 0) {
            return chartStatKeys.map((key) => ({
                stat: currentStats[key] ?? "",
                A: 0,
                B: 0,
                fullMark: 100,
            }));
        }

        if (selected.length === 1) {
            const [pA] = selected;
            return chartStatKeys.map((key) => {
                const aVal = (pA[key] as number) ?? 0;
                const aPercentage = calculatePercentage(aVal, key);
                return {
                    stat: currentStats[key] ?? "",
                    A: aPercentage,
                    B: 0,
                    fullMark: 100,
                    aRaw: aVal,
                };
            });
        }

        return chartStatKeys.map((key) => {
            const aVal = selected[0] ? ((selected[0][key] as number) ?? 0) : 0;
            const bVal = selected[1] ? ((selected[1][key] as number) ?? 0) : 0;
            const aPercentage = calculatePercentage(aVal, key);
            const bPercentage = calculatePercentage(bVal, key);
            return {
                stat: currentStats[key] ?? "",
                A: aPercentage,
                B: bPercentage,
                fullMark: 100,
                aRaw: aVal,
                bRaw: bVal,
            };
        });
    }, [selected, chartStatKeys, currentStats, maxPoints]);

    if (isLoading) {
        return (
            <div className="w-full flex-1 min-h-0 flex items-center justify-center p-4 h-[283px]">
                <Loader message="Loading comparison data..." />
            </div>
        );
    }

    return (
        <>
            {!teamGroups && playersForDropdown.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                    <span className="text-center p-4 text-on-surface-variant mb-2">No comparison data available</span>
                    {matchIds.length === 0 && (
                        <span className="text-sm text-on-surface-variant">
                            Please select matches using the "Select Matches" button in the header
                        </span>
                    )}
                </div>
            ) : (
                <div className="flex flex-col">
                    {teamGroups && (
                        <div className="flex items-center gap-2 mb-3">
                            <span className="text-xs text-on-surface-variant flex-shrink-0">Filter by team:</span>
                            {teamGroups.map((group, i) => (
                                <button
                                    key={i}
                                    onClick={group.onActivate}
                                    disabled={group.isLoading}
                                    className={`px-3 py-1.5 text-sm rounded-md border transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${
                                        group.isActive
                                            ? "bg-blue-600 text-white border-blue-500"
                                            : "bg-transparent text-on-surface border-outline-variant/20 hover:border-outline-variant/40"
                                    }`}
                                >
                                    {group.isLoading ? "Loading..." : group.label}
                                </button>
                            ))}
                        </div>
                    )}
                    <div id="onboarding-compare-player-select">
                        <ComparisonEngineMultiSelect
                            data={playersForDropdown}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    </div>
                    <div id="onboarding-compare-stats-table-mobile" className="md:hidden mt-4">
                        <ComparisonStatsMobileCards
                            data={
                                rowData as {
                                    stat: string;
                                    playerA: string | number;
                                    playerB: string | number;
                                    isTotal?: boolean;
                                }[]
                            }
                            playerAName={playerAName}
                            playerBName={playerBName}
                            viewMode={viewMode}
                            isLoading={isLoading}
                        />
                    </div>
                    <div className="hidden md:grid grid-cols-1 xl:grid-cols-[minmax(50%,max-content)_1fr] gap-4 mt-4 items-start">
                        <div id="onboarding-compare-stats-table" className="pt-6">
                            <ComparisonEngineStatsTable columns={columns} data={rowData} />
                        </div>
                        <div id="onboarding-compare-radar" className="h-[250px] sm:h-[350px] min-w-0">
                            <ComparisonEngineChart
                                data={chartData}
                                playerAName={playerAName}
                                playerBName={playerBName}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ComparisonEngine;
